/**
 * Attorney Sworn Proof of Loss Generator
 *
 * Surfaces the existing /attorney-tools/proof-of-loss/* backend (generator +
 * exports) which had no UI — attorneys could not produce a sworn POL from the
 * product despite the backend being complete + tested. Mirrors the
 * DemandLetterGenerator page pattern (case/context auto-population, AttorneyLayout,
 * PDF/DOCX export blobs).
 *
 * Forensic-correctness: every sworn dollar figure comes from the BI engine
 * (`calculation_results`), never fabricated. Figures the engine didn't provide
 * render as [NOT AVAILABLE] server-side, and the generator's `review_sections`
 * (jurat / signature / amount) are surfaced prominently — a sworn document must
 * be human-reviewed and executed, never auto-completed.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft, ScrollText, ShieldAlert, AlertTriangle, Copy, Download,
  Check, Loader2,
} from 'lucide-react';
import AttorneyLayout from '../components/AttorneyLayout';
import { getAuthHeaders } from '../lib/authHeaders';
import { FinancialValue } from '../components/shared/FinancialValue';
import { parseGenerationBlock, attestationBlockLine, responseErrorMessage } from '../services/generationGate';
import LetterheadGateNotice from '../components/attorney/LetterheadGateNotice';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const POL_TYPES = [
  { type: 'sworn', name: 'Sworn Statement in Proof of Loss', description: 'Standard sworn POL stating the amount claimed for a covered loss' },
  { type: 'partial', name: 'Partial Proof of Loss', description: 'Interim POL filed while the loss is still developing' },
  { type: 'amended', name: 'Amended Proof of Loss', description: 'Amends and supersedes a previously submitted POL' },
  { type: 'supplemental', name: 'Supplemental Proof of Loss', description: 'Supplements a prior POL with additional loss' },
];

// Optional itemization fields. Left blank => omitted (server renders
// [NOT AVAILABLE]); NEVER defaulted to 0, which would fabricate a sworn figure.
const ITEMIZATION_FIELDS: { key: string; label: string }[] = [
  { key: 'revenue_loss', label: 'Business income loss' },
  { key: 'continuing_expenses', label: 'Continuing expenses' },
  { key: 'extra_expenses', label: 'Extra expense' },
  { key: 'extended_period_indemnity', label: 'Extended period of indemnity' },
  { key: 'deductible', label: 'Deductible' },
];

interface ProofOfLossGeneratorProps {
  /** Explicit case id (e.g. from an embedding page's case picker). Falls back
   * to the `:caseId` route param when omitted, so this component behaves
   * identically at its own route (`/attorney/case/:caseId/proof-of-loss`). */
  caseId?: string;
}

export default function ProofOfLossGenerator({ caseId: propCaseId }: ProofOfLossGeneratorProps = {}) {
  const { caseId: paramCaseId } = useParams<{ caseId: string }>();
  const navigate = useNavigate();
  const caseId = propCaseId ?? paramCaseId;

  const [polType, setPolType] = useState('sworn');
  const [priorPolDate, setPriorPolDate] = useState('');

  const [caseData, setCaseData] = useState({
    insured_name: '',
    business_name: '',
    carrier_name: '',
    policy_number: '',
    claim_number: '',
    event_type: 'fire',
    event_date: '',
    state: '',
    claim_amount: 0,
  });

  // Engine-sourced final figure (read-only provenance) + optional itemization
  // the attorney can supply. Both feed calculation_results / loss_breakdown.
  const [engineClaimAmount, setEngineClaimAmount] = useState<number | null>(null);
  const [itemization, setItemization] = useState<Record<string, string>>({});

  const [loading, setLoading] = useState(false);
  const [loadingCase, setLoadingCase] = useState(true);
  const [generatedPol, setGeneratedPol] = useState<string | null>(null);
  const [reviewSections, setReviewSections] = useState<any[]>([]);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState('');
  const [blockingPlaceholders, setBlockingPlaceholders] = useState<string[]>([]);

  // Load case data on mount
  useEffect(() => {
    const fetchCaseData = async () => {
      if (!caseId) { setLoadingCase(false); return; }
      const authHeaders = await getAuthHeaders();
      if (!authHeaders.Authorization) { navigate('/attorney/login'); return; }
      try {
        const response = await fetch(`${BACKEND_URL}/api/v1/attorney/case/${caseId}`, {
          headers: authHeaders,
        });
        if (response.status === 401) {
          localStorage.removeItem('attorney_token');
          navigate('/attorney/login');
          return;
        }
        if (response.ok) {
          const data = await response.json();
          setCaseData((prev) => ({
            ...prev,
            insured_name: data.client_name || '',
            business_name: data.business_name || data.client_name || '',
            carrier_name: data.policy_coverage?.insurer_name || data.carrier || '',
            claim_number: data.claim_number || '',
            policy_number: data.policy_coverage?.policy_number || data.policy_number || '',
            claim_amount: data.total_demand || 0,
            event_type: data.event_type || 'fire',
            // CaseDetailResponse (backend/app/api/v1/attorney.py) exposes
            // `event_date`, not `loss_date` — the latter isn't on the response
            // model, so this always rendered blank.
            event_date: data.event_date || '',
            state: data.state || '',
          }));
          // NOTE: do NOT seed the sworn total (engineClaimAmount) from this
          // endpoint's `total_demand` — it is the stale result.final_claim_amount
          // snapshot (attorney.py CaseDetailResponse), and racing it against the
          // canonical /context feed could let the stale figure win. The sworn
          // Proof of Loss total comes SOLELY from the canonical /context
          // claim_amount (policy_deductions.net_claim) below. `claim_amount`
          // above only pre-fills the editable form field for display.
        }
      } catch (err) {
        console.error('Failed to load case:', err);
      } finally {
        setLoadingCase(false);
      }
    };
    fetchCaseData();
  }, [caseId, navigate]);

  // Fetch case context for the engine's final claim amount (the sworn total).
  useEffect(() => {
    if (!caseId) return;
    getAuthHeaders().then((authHeaders) => fetch(`${BACKEND_URL}/api/v1/attorney/case/${caseId}/context`, {
      headers: authHeaders,
    }))
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => {
        if (!data) return;
        const engineTotal = typeof data.claim_amount === 'number' ? data.claim_amount : null;
        setCaseData((prev) => ({
          ...prev,
          // /context returns `event_start_date`, not `event_date`.
          event_date: prev.event_date || data.event_start_date || '',
          claim_number: prev.claim_number || data.claim_number || '',
          carrier_name: prev.carrier_name || data.carrier || '',
          insured_name: prev.insured_name || data.client_name || '',
          business_name: prev.business_name || data.business_name || '',
          policy_number: prev.policy_number || data.policy_number || '',
          state: prev.state || data.state || '',
          claim_amount: prev.claim_amount || engineTotal || 0,
        }));
        if (engineTotal && engineTotal > 0) setEngineClaimAmount(engineTotal);
      })
      .catch((err) => console.error('Failed to load case context:', err));
  }, [caseId]);

  const handleInputChange = (field: string, value: string | number) => {
    setCaseData((prev) => ({ ...prev, [field]: value }));
  };

  // Build calculation_results from engine-sourced + attorney-supplied figures.
  // Only includes fields that are actually present — a blank field is omitted,
  // never coerced to 0, so the sworn POL renders [NOT AVAILABLE] rather than a
  // fabricated figure.
  const buildCalculationResults = useCallback(() => {
    const totals: Record<string, number> = {};
    for (const f of ITEMIZATION_FIELDS) {
      const raw = itemization[f.key];
      if (raw !== undefined && raw !== '' && !Number.isNaN(Number(raw))) {
        totals[f.key] = Number(raw);
      }
    }
    const finalAmount = engineClaimAmount ?? (caseData.claim_amount || null);
    if (finalAmount) totals.final_claim_amount = finalAmount;
    return Object.keys(totals).length ? { totals } : null;
  }, [itemization, engineClaimAmount, caseData.claim_amount]);

  const requestBody = useCallback(
    (outputFormat: string) => ({
      pol_type: polType,
      case_data: caseData,
      calculation_results: buildCalculationResults(),
      prior_pol_date: priorPolDate || null,
      output_format: outputFormat,
      report_id: caseId || null,
    }),
    [polType, caseData, buildCalculationResults, priorPolDate, caseId],
  );

  const generatePol = async () => {
    setLoading(true);
    setError('');
    setBlockingPlaceholders([]);
    setGeneratedPol(null);
    try {
      const response = await fetch(`${BACKEND_URL}/api/v1/attorney-tools/proof-of-loss/generate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(await getAuthHeaders()) },
        body: JSON.stringify(requestBody('text')),
      });
      if (!response.ok) {
        // Structured provenance-gate 412 → parse it (not "[object Object]") so the real
        // message + placeholders show and a letterhead gap can deep-link the fix.
        const body = await response.json().catch(() => ({}));
        const block = parseGenerationBlock(body, 'Failed to generate proof of loss');
        setError(block.kind === 'not_attested' ? attestationBlockLine(block) : block.message);
        setBlockingPlaceholders(block.blockingPlaceholders);
        return;
      }
      const data = await response.json();
      setGeneratedPol(data.formatted_pol);
      setReviewSections(data.review_sections || []);
    } catch (err: any) {
      setError(err.message || 'Failed to generate proof of loss');
    } finally {
      setLoading(false);
    }
  };

  const downloadBlob = useCallback(
    async (path: string, ext: string, mime: string) => {
      try {
        const response = await fetch(`${BACKEND_URL}/api/v1/attorney-tools/proof-of-loss/${path}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', ...(await getAuthHeaders()) },
          body: JSON.stringify(requestBody('text')),
        });
        if (!response.ok) throw new Error(await responseErrorMessage(response, `Failed to download ${ext.toUpperCase()}`));
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `proof_of_loss_${caseData.claim_number || 'draft'}_${new Date().toISOString().split('T')[0]}.${ext}`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
      } catch (err: any) {
        setError(err.message || `Failed to download ${ext.toUpperCase()}`);
      }
    },
    [requestBody, caseData.claim_number],
  );

  const copyToClipboard = useCallback(() => {
    if (generatedPol) {
      navigator.clipboard.writeText(generatedPol);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  }, [generatedPol]);

  const needsPriorDate = polType === 'amended' || polType === 'supplemental';
  const criticalReviews = reviewSections.filter(
    (r) => (r?.severity || r?.priority || '').toString().toLowerCase() === 'critical',
  );

  if (loadingCase) {
    return (
      <AttorneyLayout>
        <div className="flex items-center justify-center min-h-[60vh]" style={{ background: 'var(--color-bg-base)' }}>
          <Loader2 className="w-8 h-8 animate-spin" style={{ color: 'var(--color-brand)' }} />
        </div>
      </AttorneyLayout>
    );
  }

  return (
    <AttorneyLayout>
      {caseId && (
        <button
          onClick={() => navigate(`/attorney/case/${caseId}`)}
          className="cs-body flex items-center gap-2 mb-6"
          style={{ color: 'var(--color-text-secondary)' }}
          data-testid="back-to-case"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Case
        </button>
      )}

      <div className="mb-8">
        <h1 className="cs-display flex items-center gap-3">
          <span className="flex-shrink-0 inline-flex items-center justify-center" style={{ background: 'var(--color-needs-human-tint)', border: '1px solid var(--color-needs-human-tint-bd)', borderRadius: 11, padding: 8 }}>
            <ScrollText className="w-6 h-6" style={{ color: 'var(--color-brand)' }} />
          </span>
          Sworn Proof of Loss Generator
        </h1>
        <p className="cs-meta mt-2">
          Generate a sworn statement in proof of loss — a common condition precedent to suit.
          Sworn figures come from the forensic engine; never fabricated.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8" data-testid="pol-generator">
        {/* Left: form */}
        <div className="space-y-6">
          <div style={{ background: 'var(--color-bg-surface)', border: '1px solid var(--color-border)', borderRadius: 14, padding: 24 }}>
            <h3 className="cs-h2 mb-4">Proof of Loss Type</h3>
            <div className="space-y-2">
              {POL_TYPES.map((pt) => (
                <label
                  key={pt.type}
                  className="flex items-center gap-3 p-3 cursor-pointer transition-colors"
                  style={{
                    borderRadius: 10,
                    border: `1px solid ${polType === pt.type ? 'var(--color-brand)' : 'var(--color-border)'}`,
                    background: polType === pt.type ? 'var(--color-needs-human-tint)' : 'var(--color-bg-elevated)',
                  }}
                  data-testid={`pol-type-${pt.type}`}
                >
                  <input
                    type="radio"
                    name="polType"
                    value={pt.type}
                    checked={polType === pt.type}
                    onChange={(e) => setPolType(e.target.value)}
                    className="sr-only"
                  />
                  <ScrollText className="w-5 h-5" style={{ color: polType === pt.type ? 'var(--color-brand)' : 'var(--color-text-muted)' }} />
                  <div>
                    <div className="cs-body" style={{ color: 'var(--color-text-primary)', fontWeight: 500 }}>{pt.name}</div>
                    <div className="cs-meta">{pt.description}</div>
                  </div>
                </label>
              ))}
            </div>
            {needsPriorDate && (
              <div className="mt-4">
                <label className="cs-label block mb-1">Prior POL date *</label>
                <input
                  type="date"
                  value={priorPolDate}
                  onChange={(e) => setPriorPolDate(e.target.value)}
                  className="cs-body w-full px-3 py-2"
                  style={{ background: 'var(--color-bg-elevated)', border: '1px solid var(--color-border)', borderRadius: 10, color: 'var(--color-text-primary)' }}
                  data-testid="input-prior-pol-date"
                />
              </div>
            )}
          </div>

          <div style={{ background: 'var(--color-bg-surface)', border: '1px solid var(--color-border)', borderRadius: 14, padding: 24 }}>
            <h3 className="cs-h2 mb-4">Case Information</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="cs-label block mb-1">Insured Name *</label>
                <input type="text" value={caseData.insured_name}
                  onChange={(e) => handleInputChange('insured_name', e.target.value)}
                  className="cs-body w-full px-3 py-2"
                  style={{ background: 'var(--color-bg-elevated)', border: '1px solid var(--color-border)', borderRadius: 10, color: 'var(--color-text-primary)' }}
                  placeholder="John Smith" data-testid="input-insured-name" />
              </div>
              <div>
                <label className="cs-label block mb-1">Business Name</label>
                <input type="text" value={caseData.business_name}
                  onChange={(e) => handleInputChange('business_name', e.target.value)}
                  className="cs-body w-full px-3 py-2"
                  style={{ background: 'var(--color-bg-elevated)', border: '1px solid var(--color-border)', borderRadius: 10, color: 'var(--color-text-primary)' }}
                  placeholder="ABC Restaurant LLC" data-testid="input-business-name" />
              </div>
              <div>
                <label className="cs-label block mb-1">Carrier Name *</label>
                <input type="text" value={caseData.carrier_name}
                  onChange={(e) => handleInputChange('carrier_name', e.target.value)}
                  className="cs-body w-full px-3 py-2"
                  style={{ background: 'var(--color-bg-elevated)', border: '1px solid var(--color-border)', borderRadius: 10, color: 'var(--color-text-primary)' }}
                  placeholder="State Farm Insurance" data-testid="input-carrier-name" />
              </div>
              <div>
                <label className="cs-label block mb-1">Policy Number</label>
                <input type="text" value={caseData.policy_number}
                  onChange={(e) => handleInputChange('policy_number', e.target.value)}
                  className="cs-body w-full px-3 py-2"
                  style={{ background: 'var(--color-bg-elevated)', border: '1px solid var(--color-border)', borderRadius: 10, color: 'var(--color-text-primary)' }}
                  placeholder="POL-12345" data-testid="input-policy-number" />
              </div>
              <div>
                <label className="cs-label block mb-1">Claim Number</label>
                <input type="text" value={caseData.claim_number}
                  onChange={(e) => handleInputChange('claim_number', e.target.value)}
                  className="cs-body w-full px-3 py-2"
                  style={{ background: 'var(--color-bg-elevated)', border: '1px solid var(--color-border)', borderRadius: 10, color: 'var(--color-text-primary)' }}
                  placeholder="CLM-2026-001" data-testid="input-claim-number" />
              </div>
              <div>
                <label className="cs-label block mb-1">Date of Loss</label>
                <input type="date" value={caseData.event_date}
                  onChange={(e) => handleInputChange('event_date', e.target.value)}
                  className="cs-body w-full px-3 py-2"
                  style={{ background: 'var(--color-bg-elevated)', border: '1px solid var(--color-border)', borderRadius: 10, color: 'var(--color-text-primary)' }}
                  data-testid="input-event-date" />
              </div>
            </div>
          </div>

          {/* Sworn figures — engine-sourced total + optional itemization */}
          <div style={{ background: 'var(--color-bg-surface)', border: '1px solid var(--color-border)', borderRadius: 14, padding: 24 }}>
            <h3 className="cs-h2 mb-1">Sworn Figures</h3>
            <p className="cs-meta mb-4">
              The total claimed comes from the forensic engine. Itemized line items are optional —
              leave blank and they render <span className="cs-figure">[NOT AVAILABLE]</span> rather
              than a fabricated figure.
            </p>
            <div className="mb-4">
              <label className="cs-label block mb-1">Total amount claimed (engine)</label>
              <div
                className="cs-figure w-full px-3 py-2"
                style={{ background: 'var(--color-bg-elevated)', border: '1px solid var(--color-border)', borderRadius: 10, color: 'var(--color-text-primary)' }}
                data-testid="engine-total"
              >
                <FinancialValue value={engineClaimAmount} cents />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              {ITEMIZATION_FIELDS.map((f) => (
                <div key={f.key}>
                  <label className="cs-label block mb-1">{f.label}</label>
                  <input
                    type="number"
                    value={itemization[f.key] ?? ''}
                    onChange={(e) => setItemization((prev) => ({ ...prev, [f.key]: e.target.value }))}
                    className="cs-figure w-full px-3 py-2"
                    style={{ background: 'var(--color-bg-elevated)', border: '1px solid var(--color-border)', borderRadius: 10, color: 'var(--color-text-primary)' }}
                    placeholder="—"
                    data-testid={`input-${f.key}`}
                  />
                </div>
              ))}
            </div>
          </div>

          <button
            onClick={generatePol}
            disabled={loading}
            className="cs-body w-full inline-flex items-center justify-center gap-2"
            style={{ padding: '12px 16px', borderRadius: 10, background: 'var(--color-brand)', color: 'var(--color-on-brand)', fontWeight: 500, opacity: loading ? 0.5 : 1, cursor: loading ? 'not-allowed' : 'pointer' }}
            data-testid="generate-pol"
          >
            {loading ? <><Loader2 className="w-5 h-5 animate-spin" /> Generating…</> : <><ScrollText className="w-5 h-5" /> Generate Proof of Loss</>}
          </button>

          {error && (
            <div className="cs-body px-4 py-3" style={{ background: 'var(--color-needs-human-tint)', border: '1px solid var(--color-needs-human-tint-bd)', borderRadius: 10, color: 'var(--color-needs-human-text)' }} data-testid="pol-error">
              {error}
              <LetterheadGateNotice blockingPlaceholders={blockingPlaceholders} />
            </div>
          )}
        </div>

        {/* Right: review flags + preview + export */}
        <div className="space-y-6">
          {/* Forensic-correctness surface: items requiring human execution before swearing */}
          {criticalReviews.length > 0 && (
            <div style={{ background: 'var(--color-needs-human-tint)', border: '1px solid var(--color-needs-human-tint-bd)', borderRadius: 14, padding: 20 }} data-testid="pol-critical-reviews">
              <div className="flex items-start gap-3">
                <ShieldAlert className="w-5 h-5 flex-shrink-0 mt-0.5" style={{ color: 'var(--color-brand)' }} />
                <div>
                  <h4 className="cs-h3" style={{ color: 'var(--color-needs-human-text)' }}>Requires execution before swearing</h4>
                  <p className="cs-body mt-1" style={{ color: 'var(--color-needs-human-text)' }}>
                    This is a sworn document. The following must be reviewed and executed by hand —
                    the generator never auto-completes a jurat, signature, or unverified figure.
                  </p>
                  <ul className="mt-2 space-y-1">
                    {criticalReviews.map((r, i) => (
                      <li key={i} className="cs-body flex items-start gap-2" style={{ color: 'var(--color-needs-human-text)' }}>
                        <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                        <span><strong>{r.section || r.title || 'Review'}:</strong> {r.message || r.note || r.description}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          )}

          {generatedPol ? (
            <div style={{ background: 'var(--color-bg-surface)', border: '1px solid var(--color-border)', borderRadius: 14, padding: 24 }} data-testid="pol-preview">
              <div className="flex items-center justify-between mb-4">
                <h3 className="cs-h2">Generated Proof of Loss</h3>
                <div className="flex items-center gap-2">
                  <button onClick={copyToClipboard} className="cs-body inline-flex items-center gap-1.5 px-3 py-1.5" style={{ background: 'var(--color-bg-elevated)', border: '1px solid var(--color-border)', borderRadius: 10, color: 'var(--color-text-secondary)' }} data-testid="copy-pol">
                    {copied ? <><Check className="w-4 h-4" style={{ color: 'var(--color-verified)' }} /> Copied</> : <><Copy className="w-4 h-4" /> Copy</>}
                  </button>
                  <button onClick={() => downloadBlob('export/pdf', 'pdf', 'application/pdf')} className="cs-body inline-flex items-center gap-1.5 px-3 py-1.5" style={{ background: 'var(--color-bg-elevated)', border: '1px solid var(--color-border)', borderRadius: 10, color: 'var(--color-text-secondary)' }} data-testid="download-pdf">
                    <Download className="w-4 h-4" /> PDF
                  </button>
                  <button onClick={() => downloadBlob('export/docx', 'docx', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document')} className="cs-body inline-flex items-center gap-1.5 px-3 py-1.5" style={{ background: 'var(--color-bg-elevated)', border: '1px solid var(--color-border)', borderRadius: 10, color: 'var(--color-text-secondary)' }} data-testid="download-docx">
                    <Download className="w-4 h-4" /> DOCX
                  </button>
                </div>
              </div>
              <pre className="cs-figure whitespace-pre-wrap p-4 max-h-[70vh] overflow-auto" style={{ background: 'var(--color-bg-elevated)', borderRadius: 10, color: 'var(--color-text-secondary)' }}>
                {generatedPol}
              </pre>
            </div>
          ) : (
            <div className="cs-body p-12 text-center" style={{ background: 'var(--color-bg-surface)', border: '1px dashed var(--color-border)', borderRadius: 14, color: 'var(--color-text-muted)' }} data-testid="pol-empty">
              <ScrollText className="w-10 h-10 mx-auto mb-3 opacity-50" />
              <p>Fill in the case details and generate a sworn proof of loss.</p>
            </div>
          )}
        </div>
      </div>
    </AttorneyLayout>
  );
}
